import { AxiosError } from 'axios'

import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query'

import { IErrorDTO } from '@/DTOs/errorDTO'

import {
  getCensusYearSummary,
  getFiles,
  getProcessingDetail,
  getProcessingUsers,
} from './services'
import {
  TCensusYearSummary,
  TGetFilesParams,
  TGetFilesResponse,
  TProcessingDetail,
  TProcessingUser,
} from './types'

export const useQueryGetFiles = (
  filters: TGetFilesParams,
  options?: UseQueryOptions<TGetFilesResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getFiles(filters)

  return useQuery<TGetFilesResponse, AxiosError<IErrorDTO>>(
    useQueryGetFiles.getFullQueryKey(filters),
    queryFunction,
    options
  )
}

useQueryGetFiles.getFullQueryKey = (filters: TGetFilesParams): QueryKey => [
  'files',
  filters,
]

useQueryGetFiles.queryKey = ['files']

export const useQueryGetProcessingUsers = (
  options?: UseQueryOptions<TProcessingUser[], AxiosError<IErrorDTO>>
) => {
  return useQuery<TProcessingUser[], AxiosError<IErrorDTO>>(
    useQueryGetProcessingUsers.queryKey,
    getProcessingUsers,
    options
  )
}

useQueryGetProcessingUsers.getFullQueryKey = (): QueryKey => [
  'prediction-processing-users',
]

useQueryGetProcessingUsers.queryKey = ['prediction-processing-users']

export const useQueryGetCensusYearSummary = (
  year: number | null,
  options?: UseQueryOptions<TCensusYearSummary, AxiosError<IErrorDTO>>
) => {
  return useQuery<TCensusYearSummary, AxiosError<IErrorDTO>>(
    useQueryGetCensusYearSummary.getFullQueryKey(year),
    () => getCensusYearSummary(year as number),
    // Sempre atual: a prévia decide se o operador confirma uma substituição.
    { enabled: year !== null, staleTime: 0, cacheTime: 0, ...options }
  )
}

useQueryGetCensusYearSummary.getFullQueryKey = (
  year: number | null
): QueryKey => ['census-year-summary', year]

export const useQueryGetProcessingDetail = (
  id: number | null,
  options?: UseQueryOptions<TProcessingDetail, AxiosError<IErrorDTO>>
) => {
  return useQuery<TProcessingDetail, AxiosError<IErrorDTO>>(
    useQueryGetProcessingDetail.getFullQueryKey(id),
    () => getProcessingDetail(id as number),
    // Busca a cada abertura: o status do envio muda enquanto ele roda.
    { enabled: id !== null, staleTime: 0, ...options }
  )
}

useQueryGetProcessingDetail.getFullQueryKey = (id: number | null): QueryKey => [
  'prediction-processing-detail',
  id,
]
